import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Handshake, Globe2, ArrowLeftRight } from "lucide-react";
import { scrollToId } from "../data/content";
import { useT } from "../i18n";

const TRUST_ICONS = [BadgeCheck, Handshake, Globe2, ArrowLeftRight];

const TILES = [
  { src: "/assets/coffee-green.jpg", label: "COFFEE" },
  { src: "/assets/tuna.jpg", label: "TUNA" },
  { src: "/assets/dragonfruit-red.jpg", label: "DRAGON FRUIT" },
  { src: "/assets/ginger.jpg", label: "GINGER" },
  { src: "/assets/cloves.jpg", label: "CLOVES" },
];

const Hero = () => {
  const t = useT();
  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-[#0B0E14]">
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-[#14532D]/20 blur-[120px]" />

      <div className="mx-auto grid min-h-screen w-full max-w-[1400px] grid-cols-1 items-center gap-12 px-4 pb-20 pt-32 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-8">
        <div>
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-7 font-mono text-[11px] uppercase tracking-[0.32em] text-gold"
          >
            {t.hero.overline}
          </motion.p>

          <h1 data-testid="hero-headline" className="mb-7">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block font-display text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              >
                {t.hero.line1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block font-display text-4xl font-semibold italic leading-[1.08] tracking-tight text-gold sm:text-5xl lg:text-[3.4rem]"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                {t.hero.line2}
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mb-3 text-base font-medium text-slate-100 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mb-9 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base"
          >
            {t.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mb-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap"
          >
            <button
              data-testid="hero-cta-products"
              onClick={() => scrollToId("#products")}
              className="group flex items-center justify-center gap-2 bg-gold px-7 py-4 font-mono text-xs tracking-[0.2em] text-[#0B132B] transition-[box-shadow] duration-300 hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
            >
              {t.hero.cta1.toUpperCase()}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("#quote")}
              className="border border-slate-600 px-7 py-4 text-center font-mono text-xs tracking-[0.2em] text-slate-200 transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              {t.hero.cta2.toUpperCase()}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-trust-indicators"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-4"
          >
            {t.hero.trust.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <div key={item} className="flex items-center gap-2">
                  <Icon size={14} className="shrink-0 text-gold" />
                  <span className="font-mono text-[10px] leading-tight tracking-[0.12em] text-slate-400">{item}</span>
                </div>
              );
            })}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.45 }}
          className="flex flex-col gap-3"
        >
          <div className="relative">
            <div className="absolute -left-3 -top-3 h-full w-full border border-gold/30" />
            <div className="clipped-frame relative aspect-[16/10] overflow-hidden">
              <img
                src="/assets/cube-pile.jpeg"
                alt="Premium Indonesian coconut charcoal briquettes by CRESSCO DAYA UTAMA"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/60 via-transparent to-transparent" />
              <p className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.28em] text-slate-200">
                COCONUT CHARCOAL BRIQUETTES
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {TILES.map((tile, i) => (
              <motion.div
                key={tile.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 + i * 0.1 }}
                className="group relative aspect-square overflow-hidden border border-slate-800"
              >
                <img
                  src={tile.src}
                  alt={`Indonesian ${tile.label.toLowerCase()} — CRESSCO DAYA UTAMA`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/80 via-transparent to-transparent" />
                <p className="absolute bottom-2 left-2.5 font-mono text-[8px] tracking-[0.22em] text-slate-200 sm:text-[9px]">
                  {tile.label}
                </p>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="relative flex aspect-square items-center justify-center border border-gold/30 bg-[#0B132B] p-3"
            >
              <p className="text-center font-mono text-[9px] leading-relaxed tracking-[0.18em] text-gold">
                {t.hero.flow}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
